import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Alert } from '@mui/material';
import Controls from '../../components/controls/Controls';
import { urlCache } from '../../config';
import { logActionType } from '../../services/MyCollections';
import { logActivity } from '../../services/ApiServices';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function SystemMaintenance({ token, userName, onNotify, darkTheme }) {
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleClearCache = async () => {
        setOpenConfirmDialog(false);
        setIsLoading(true);

        try {
            const response = await fetch(`${urlCache}/clear`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                logActivity(logActionType.CLEAR_CACHE, userName, token);
                if (onNotify) {
                    onNotify('Pamięć podręczna (cache) została pomyślnie wyczyszczona.', 'success');
                }
            } else {
                if (onNotify) {
                    onNotify('Wystąpił błąd podczas czyszczenia pamięci podręcznej.', 'error');
                }
            }
        } catch (error) {
            console.error('Błąd podczas czyszczenia cache:', error);
            if (onNotify) {
                onNotify('Błąd sieci podczas czyszczenia cache.', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>
                    Konserwacja Systemu
                </Typography>
                <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                    Narzędzia diagnostyczne oraz zarządzanie pamięcią podręczną aplikacji Pelikan.
                </Typography>
            </Box>

            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.4)' : '#FFFFFF',
                    mb: 3
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                                color: '#FFFFFF',
                                flexShrink: 0,
                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                            }}
                        >
                            <CleaningServicesIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A', mb: 0.5 }}>
                                Pamięć podręczna systemu (Cache)
                            </Typography>
                            <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#475569', mb: 2 }}>
                                Czyszczenie pamięci podręcznej usuwa zbuforowane słowniki, cenniki oraz tymczasowe dane sesji po stronie serwera. Zalecane po aktualizacji stawek lub konfiguracji systemowych.
                            </Typography>

                            <Alert
                                icon={<InfoOutlinedIcon fontSize="small" />}
                                severity="info"
                                sx={{
                                    mb: 2.5,
                                    borderRadius: 1.5,
                                    backgroundColor: darkTheme ? 'rgba(59, 130, 246, 0.1)' : undefined,
                                    color: darkTheme ? '#93C5FD' : undefined
                                }}
                            >
                                Ta operacja jest bezpieczna dla integralności bazy danych, ale może chwilowo wydłużyć czas ładowania kolejnych zapytań użytkowników.
                            </Alert>

                            <Controls.PremiumButton
                                variant="red"
                                label={isLoading ? "Czyszczenie..." : "Wyczyść pamięć podręczną (Cache)"}
                                icon="fas fa-broom"
                                onClick={() => setOpenConfirmDialog(true)}
                                disabled={isLoading}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Modalne potwierdzenie czyszczenia cache */}
            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 2.5,
                        p: 1,
                        backgroundColor: darkTheme ? '#1E293B' : '#FFFFFF',
                        color: darkTheme ? '#F8FAFC' : '#0F172A'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Potwierdzenie czyszczenia cache
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: darkTheme ? '#CBD5E1' : '#475569' }}>
                        Czy na pewno chcesz wyczyścić całą pamięć podręczną systemu Pelikan? Zmiana wejdzie w życie natychmiast dla wszystkich zalogowanych użytkowników.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setOpenConfirmDialog(false)}
                        sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClearCache}
                        autoFocus
                        sx={{ borderRadius: 1.5 }}
                    >
                        Tak, wyczyść cache
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
